(ns practitest.frontend.testset2.testset-grid
  (:require [practitest.frontend.common-entities :refer [batch-navigate!
                                                         batch-open-in-new-tab!
                                                         common-index-grid-props
                                                         grid-columns]
             :as common-entities]
            [practitest.frontend.common-entities.events :as common-events]
            [practitest.frontend.common-entities.index :as common-index]
            [practitest.frontend.common-entities.metadata :as metadata]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.format-utils :as fu]
            [practitest.frontend.common.i18n :refer [format-translation
                                                     get-translation]]
            [practitest.frontend.common.mixpanel :as mixpanel]
            [practitest.frontend.common.permissions :as permissions]
            [practitest.frontend.common.re-frame :refer [partial-dispatch
                                                         partial-subscribe]]
            [practitest.frontend.components.grid.events :as grid.events]
            [practitest.frontend.components.grid.subs :as grid.subs]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components2.entity-form.forms :as forms]
            [practitest.frontend.components2.grid :as grid2]
            [practitest.frontend.components2.grid.fields :refer [transform-fields]]
            [practitest.frontend.components2.grid.row-menu :refer [row-menu]]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.subs :as subs]
            [practitest.frontend.testset2.events :as testset-events]
            [practitest.frontend.testset2.instance-view-toggle :refer [instance-view-toggle-button]]
            [practitest.frontend.testset2.subs :as testset-subs]
            [re-frame.core :as re-frame]))

;; Import constants from index namespace
(def current-entity :testset)
(def state-key testset-events/testset-index-state-key)
(def table-id (get-in metadata/entities-metadata [current-entity :table-id]))
(def edit-permission (get-in metadata/entities-metadata [:testset :edit-permissions]))
(def admin-permission (get-in metadata/entities-metadata [:testset :delete-permissions]))
(def default-columns
  (get-in metadata/entities-metadata [current-entity :default-columns]))

(defn dropdown-actions-menu [selection]
  (let [disabled? (not (permissions/has-permission? edit-permission))
        empty-selection? (grid.subs/empty-selection? selection)
        current-view @(re-frame/subscribe [::grid.subs/current-view current-entity state-key])
        track-fn #(mixpanel/track "Grid Actions" {"Action" %})
        filters (common-index/get-entity-filters current-entity)
        batch-action-fn #(batch-navigate! (broutes/get-url-view %) selection current-view {:filter filters})
        permutations-enabled? @(re-frame/subscribe [::grid.subs/permutations-enabled?])
        automation-enabled? @(re-frame/subscribe [::grid.subs/automation-enabled?])]
    [common-index/dropdown-actions-menu
     [{:text     (get-translation "entity.button.find_and_replace" "Find & Replace")
       :on-click (fn []
                   (track-fn "Find & Replace")
                   (batch-action-fn "suites#find_and_replace"))
       :icon     [icons/find-and-replace]
       :disabled? empty-selection?}
      {:text     (get-translation "generic.button.delete" "Delete")
       :on-click  #(batch-action-fn "suites#batch_delete")
       :icon     [icons/mux-trash]
       :disabled? empty-selection?}
      {:text     (get-translation "testset.button.restore" "Restore")
       :on-click (fn []
                   (track-fn "Restore")
                   (dom/navigate! (broutes/get-url-view "suites#restore")))
       :icon     [icons/restore]}
      {:text     (get-translation "generic.button.print" "Print")
       :on-click (fn []
                   (track-fn "Print")
                   (batch-open-in-new-tab!
                    (broutes/get-url-view "suites#print") selection current-view {:filter filters}))
       :icon     [icons/printer]
       :disabled? empty-selection?}
      {:text (get-translation "generic.button.clone" "Clone")
       :icon [icons/mux-copy]
       :on-click (fn []
                   (track-fn "Clone TestSet")
                   (batch-action-fn "suites#batch_clone"))
       :disabled? empty-selection?}
      {:text  (get-translation "testset.button.automation" "Automation")
       :test-id "testsets-grid-automation"
       :href (when automation-enabled?
               (broutes/get-url-view "suites#automation"
                                     {:p_id (:id @(re-frame/subscribe [::subs/current-project]))}))
       :on-click (fn []
                   (track-fn "Automation"))
       :icon [icons/mux-automation-icon]
       :disabled? (not automation-enabled?)}
      {:text (get-translation "testset.button.permutations" "Permutations")
       :test-id "testsets-grid-permutations"
       :on-click (fn []
                   (track-fn "Permutation")
                   (batch-navigate! (broutes/get-url-view "suites#create_permutations")
                                    selection current-view))
       :icon [icons/mux-permutations]
       :disabled? (or empty-selection?
                      (not permutations-enabled?))}]
     disabled?]))

(defn grid-buttons []
  (let [has-edit-permissions?      (permissions/has-permission? edit-permission)
        selection @(re-frame/subscribe [::grid.subs/selection [current-entity testset-events/testset-index-state-key]])
        current-view @(re-frame/subscribe [::grid.subs/current-view current-entity state-key])]
    [:div.entities-grid-toolbar.all-available-width
      [:div {:style {:margin-right "auto"}}
        [instance-view-toggle-button]]
     [common-index/grid-buttons
      [(common-index/batch-edit-button-data current-entity selection current-view has-edit-permissions?)
       (common-index/new-entity-button-data current-entity)]]
     (dropdown-actions-menu selection)]))

(re-frame/reg-sub
 ::filter_columns
 :<- [::subs/entity :testset]
 (fn [entity [_ state-key]]
   (get-in entity [state-key :filter-cols])))

(defmethod grid.subs/columns :testset-columns [[_current-project entity-fields fields users groups folders] [_ [_entity _]]]
  (concat
   [{:type :url
     :field :name
     :default-width 200
     :autofilter? true
     :header "Name"}
    {:type          :number
     :field         :instances_count
     :default-width 120
     :default-show? false
     :autofilter?   true
     :header        "Instances Count"}
    {:type          :status-bar
     :default-width 200
     :autofilter?   false
     :sortable?     false
     :header        "Run Status Bar"
     :field         :run_status_bar}
    {:type          :linked-entities
     :autofilter?   false
     :sortable?     false
     :header        "Milestone"
     :field         :linked_milestones}]
   (transform-fields [:run_status :last_run :assigned_to :run_status_bar]
                     (dissoc (:testset entity-fields) :description)
                     fields users groups folders)))

(defn grid [grid-state]
  (let [current-tab               (re-frame/subscribe [::testset-subs/current-tab])
        has-edit-permissions      (permissions/has-permission? edit-permission)
        has-admin-permissions     (permissions/has-permission? admin-permission)
        permutations-enabled?     @(re-frame/subscribe [::grid.subs/permutations-enabled?])
        columns-id (get-in metadata/entities-metadata [current-entity :columns-id])
        all-columns (concat default-columns
                            (grid-columns @(re-frame/subscribe [::grid.subs/columns [current-entity columns-id]])
                                          @(re-frame/subscribe [::filter_columns state-key])))
        grid-props {:columns      (map #(assoc % :default-show? true) all-columns)
                    :post-columns [{:header        ""
                                    :field         :actions
                                    :default-width 50
                                    :width         50
                                    :type          :custom
                                    :text-align    :center
                                    :resizable?    false
                                    :sortable?     false
                                    :systemcol?    true
                                    :class         ["action-menu"]
                                    :cell-fn       (fn [_ row]
                                                     [row-menu row (not has-edit-permissions)
                                                      [{:label    (get-translation "generic.button.edit" "Edit")
                                                        :icon     icons/mux-pencil
                                                        :href     (:url row)
                                                        :disabled?    (not has-edit-permissions)
                                                        ;; TODO: fix this
                                                        :on-click (fn [])}

                                                       {:label        (get-translation "generic.button.delete" "Delete")
                                                        :icon         icons/mux-trash
                                                        :disabled?    (not has-admin-permissions)
                                                        :on-click     (fn []
                                                                        (mixpanel/track "Grid Actions" {"Action" "Delete TestSet"})
                                                                        (re-frame/dispatch [::common-events/delete-entity (:id row) state-key grid-state]))
                                                        :confirmation {:title     (get-translation "testset.confirm.delete.title"
                                                                                                   "Delete Test Set?")
                                                                       :body      (format-translation
                                                                                   (get-translation "entity.confirm.delete.body"
                                                                                                    "\"%{object-name}\" will be deleted permanently.")
                                                                                   :object-name (fu/truncate-text (:title row) 30))
                                                                       :yes-label (get-translation "generic.button.delete"
                                                                                                   "Delete")
                                                                       :no-label  (get-translation "generic.button.cancel"
                                                                                                   "Cancel")}}

                                                       {:label    (get-translation "generic.button.print" "Print")
                                                        :icon     icons/printer
                                                        :disabled? false
                                                        :on-click (fn []
                                                                    (mixpanel/track "Grid Actions" {"Action" "Print TestSet"})
                                                                    (batch-open-in-new-tab! (broutes/get-url-view "suites#print") #{(:id row)} nil))}

                                                       {:label    (get-translation "generic.button.clone" "Clone")
                                                        :icon     icons/mux-copy
                                                        :disabled? (not has-edit-permissions)  
                                                        :on-click (fn []
                                                                    (mixpanel/track "Grid Actions" {"Action" "Clone TestSet"})
                                                                    (dom/form-navigate! (broutes/get-url-view "suites#clone_entity" {:id (:id row)}) {"_method" "post"}))}

                                                       {:label    (get-translation "testset.button.permutations" "Permutations")
                                                        :icon     icons/mux-permutations
                                                        :disabled? (or (not permutations-enabled?)
                                                                       (not has-edit-permissions))
                                                        :on-click (fn []
                                                                    (mixpanel/track "Grid Actions" {"Action" "Permutation"})
                                                                    (batch-navigate! (broutes/get-url-view "suites#create_permutations") #{(:id row)} nil))}]])}]
                    :current-view           @current-tab
                    :loading?              @(re-frame/subscribe [::subs/progress? ::common-events/fetch-entity-list])
                    :grid-state            grid-state  ; TODO: Check if can be removed because :local-state-updating
                    :local-state-updating  grid-state
                    :selected?             #(re-frame/subscribe [::grid.subs/selected? [current-entity state-key] %])
                    :all-selected?         #(re-frame/subscribe [::grid.subs/all-selected? [current-entity state-key] %])
                    :toggle-selection      (partial-dispatch [::grid.events/toggle-selection [current-entity state-key]])
                    :toggle-selection-page (partial-dispatch [::grid.events/toggle-selection-page [current-entity state-key]])
                    :on-query-changed      (if (= @current-tab "filters")
                                             (partial-dispatch [::common-events/fetch-entity-list current-entity state-key])
                                             (partial-dispatch [::common-events/fetch-entity-list-by-folder current-entity state-key]))
                    :current-page          @(re-frame/subscribe [::grid.subs/entities-current-page current-entity state-key])
                    :total-count           @(re-frame/subscribe [::grid.subs/entities-total-count current-entity state-key])
                    :full-data?            @(re-frame/subscribe [::grid.subs/entities-full-data? current-entity state-key])

                    ;; For filters
                    :get-field-props       (partial-subscribe [::forms/field-props :testset])
                    :grid-toolbar          grid-buttons
                    :on-expand-click       (partial-dispatch [::common-events/toggle-expanded current-entity state-key])
                    :expanded?             @(re-frame/subscribe [::grid.subs/expanded? current-entity state-key])
                    :show-save-fast-filter? true}
        grid-props (merge (common-index-grid-props current-entity state-key table-id)
                          grid-props)]
    [:div.entities-grid {:class (if (:expanded? grid-props) "col" "")}
     [grid2/grid grid-props]]))
